/**
 * Paywall Publisher
 *
 * The shared half of the feature-gate reaction
 * (docs/prd/prd-feature-gate-refactor.md, D1/D3/D4). The request coordinator
 * publishes every typed 402 here; this module applies the restraint rules and
 * only then hands the error to the platform's registered `PaywallPresenter`.
 * The rules live HERE, not in each presenter, so a platform inherits them by
 * registering one object (D6) — mobile never re-implements dedupe or claims.
 *
 * Restraint rules, in order:
 *
 * 1. **No presenter, no work.** An unregistered platform behaves exactly as it
 *    did before the seam existed.
 * 2. **Dedupe by feature slug within a short window** (D3). Two requests
 *    refused for the same feature are one paywall — the case it was built
 *    for is the feed's `/friend/feed` + `/friend/feed/summary`, both checked
 *    on `activity_feed` (Free since 2026-10-22) and both fired on mount. The
 *    slug is recorded when a presentation LANDS, not when it is scheduled: a
 *    presenter that drops a refusal (a read from a screen the reader has
 *    left) leaves the slug free for the current screen's own refusal, which
 *    may be only milliseconds behind.
 * 3. **Deferred delivery** (D4). Presentation is scheduled on a macrotask, not
 *    a microtask and not synchronously: a local `PaywallGate`'s claim needs
 *    the rejection to travel hook → state → render → throw → boundary, and
 *    that whole React cycle flushes in microtasks — so a macrotask is the
 *    earliest moment a claim can be trusted to have landed, and anything
 *    earlier would present before the boundary could speak.
 * 4. **Claims win** (D4). A local gate that caught the error calls
 *    `claimPaywallError`; the scheduled presentation drops a claimed error.
 *    Claims are keyed on the error INSTANCE (`rethrowTyped` re-throws by
 *    identity, so the object the boundary catches is the object published
 *    here), plus the instance's feature slug for the same window — so a burst
 *    where one same-feature rejection is claimed and its twin was deduped
 *    stays silent instead of presenting a feature a gate already rendered.
 *    A gate never says which feature it handles — it reads the slug off what
 *    it caught — so no route/feature map is reintroduced.
 *
 * Presenter failures are swallowed: presentation is a side effect of a request
 * failing and must never alter how the failure propagates to its caller.
 */

import { getPaywallPresenter } from '../../di/container';
import { isPaymentRequiredError } from '../../types/api/errors';
import { dayjs } from '../../utils/date';

import type { PaymentRequiredError } from '../../types/api/errors';

/**
 * How long two same-feature refusals count as one paywall, and how long a
 * local gate's claim keeps its feature quiet. Long enough to cover a page
 * load's burst of parallel requests; short enough that a later, genuinely new
 * refusal (a navigation, a retry minutes on) presents again.
 */
const DEDUPE_WINDOW_MS = 1000;

/** Errors a local gate has claimed — identity-keyed, no plumbing needed. */
let claimedErrors = new WeakSet<PaymentRequiredError>();

/** Feature slugs a local gate has claimed, by claim time. */
let claimedSlugs = new Map<string, number>();

/** Dedupe keys already published, by publish time. */
let publishedKeys = new Map<string, number>();

/**
 * Invalidates presentations scheduled before a reset so a test's pending
 * timeout can never fire into the next test's presenter.
 */
let generation = 0;

/**
 * One key per distinct paywall: the feature slug when the server named one,
 * else the route path, so two bare-402 features can't collapse into each
 * other.
 */
function dedupeKey(error: PaymentRequiredError): string {
  if (error.feature?.slug) {
    return `feature:${error.feature.slug}`;
  }

  const url = (error as PaymentRequiredError & { url?: string }).url;
  return url ? `url:${url.split('?')[0]}` : 'unknown';
}

/** Drop entries older than the window so the maps never grow unbounded. */
function prune(map: Map<string, number>, now: number): void {
  for (const [key, at] of map) {
    if (now - at >= DEDUPE_WINDOW_MS) {
      map.delete(key);
    }
  }
}

/**
 * Publish a failed request's typed error toward the platform presenter.
 *
 * Called by the request coordinator for EVERY typed error it returns; anything
 * that is not a `PaymentRequiredError` is ignored here, which keeps the
 * trigger keyed on status alone and the call site unconditional.
 */
export function publishPaywall(error: unknown): void {
  if (!isPaymentRequiredError(error)) return;
  if (getPaywallPresenter() === undefined) return;

  const now = dayjs().valueOf();
  const key = dedupeKey(error);

  prune(publishedKeys, now);
  if (publishedKeys.has(key)) return;

  const scheduledGeneration = generation;
  setTimeout(() => {
    if (scheduledGeneration !== generation) return;
    if (claimedErrors.has(error)) return;

    const firedAt = dayjs().valueOf();
    prune(claimedSlugs, firedAt);
    if (claimedSlugs.has(key)) return;

    // Checked again here because the key is recorded only below, once a
    // presentation lands: two same-slug refusals published in one tick both
    // reach this timer, the first presents and records, the second finds it.
    prune(publishedKeys, firedAt);
    if (publishedKeys.has(key)) return;

    // Re-resolved at fire time: the presenter may have (un)registered since.
    const presenter = getPaywallPresenter();
    if (presenter === undefined) return;

    try {
      // `false` is a drop (a read from a screen the reader has left): nothing
      // was shown, so the slug stays free for the current screen's refusal.
      if (presenter.present(error) !== false) {
        publishedKeys.set(key, firedAt);
      }
    } catch {
      // Presenting must never break the failure path it rides on.
    }
  }, 0);
}

/**
 * Whether a refused read was sent by a screen the reader has already left.
 *
 * Navigating away does not cancel in-flight reads, so a slow page's `GET` can
 * 402 after the next page is up — and judged against that page, it reads as
 * the new page's initial load and replaces a page that asked for nothing. A
 * presenter drops such a refusal: the screen that sent it is gone, along with
 * any gate that would have rendered it.
 *
 * Reads only. A mutation the reader started still deserves its answer after
 * they move on. An error with no `requestStartedAt` is never stale.
 *
 * @param lastNavigationAt When the presenter last saw a navigation (epoch ms).
 *   Stamp it before the new screen's effects run, or its own first reads
 *   count as stale.
 */
export function isRefusalFromEarlierView(
  error: PaymentRequiredError,
  lastNavigationAt: number
): boolean {
  return (
    error.method === 'GET' &&
    error.requestStartedAt !== undefined &&
    error.requestStartedAt < lastNavigationAt
  );
}

/**
 * Mark an error as handled by a local gate (`PaywallBoundary`) so the
 * scheduled global presentation drops it. Safe to call with anything a
 * boundary catches — non-paywall errors are ignored.
 */
export function claimPaywallError(error: unknown): void {
  if (!isPaymentRequiredError(error)) return;

  claimedErrors.add(error);

  const now = dayjs().valueOf();
  prune(claimedSlugs, now);
  claimedSlugs.set(dedupeKey(error), now);
}

/**
 * Reset all publisher state AND invalidate any scheduled presentation. Test
 * isolation only — the publisher is app-wide state, exactly the leak the PRD's
 * risk list calls out.
 */
export function resetPaywallPublisher(): void {
  generation += 1;
  claimedErrors = new WeakSet<PaymentRequiredError>();
  claimedSlugs = new Map<string, number>();
  publishedKeys = new Map<string, number>();
}
